/**
 * BrowserPedometer — in-browser step counter using the DeviceMotion
 * accelerometer API. Works on most modern phones; iOS 13+ requires an
 * explicit user-gesture permission prompt.
 *
 * Algorithm: low-pass filter the magnitude of acceleration, then count
 * peaks above a threshold with a 250 ms cool-down. Empirically ~85%
 * accurate when the phone is in pocket or hand.
 *
 * Submits the session's accumulated steps to the backend via the
 * `onSubmit` callback when the user taps "Stop & Log".
 */
import { useEffect, useRef, useState } from "react";
import { Button } from "../components/ui/button";

const PEAK_THRESHOLD = 1.2;       // multiples of g (≈11.8 m/s²)
const COOLDOWN_MS = 250;          // min ms between counted steps

export default function BrowserPedometer({ onSubmit, disabled }) {
  const [permission, setPermission] = useState("prompt");
  const [running, setRunning] = useState(false);
  const [steps, setSteps] = useState(0);
  const [startedAt, setStartedAt] = useState(null);
  const lastStepRef = useRef(0);
  const handlerRef = useRef(null);

  useEffect(() => () => detach(), []);

  function detach() {
    if (handlerRef.current) {
      window.removeEventListener("devicemotion", handlerRef.current);
      handlerRef.current = null;
    }
  }

  async function requestPermission() {
    // iOS 13+ pattern
    const DM = typeof DeviceMotionEvent !== "undefined" && DeviceMotionEvent;
    if (DM && typeof DM.requestPermission === "function") {
      try {
        const res = await DM.requestPermission();
        setPermission(res);
        return res === "granted";
      } catch {
        setPermission("denied");
        return false;
      }
    }
    setPermission("granted");
    return true;
  }

  async function start() {
    const ok = await requestPermission();
    if (!ok) return;
    setSteps(0);
    setStartedAt(Date.now());
    lastStepRef.current = 0;
    const onMotion = (e) => {
      const a = e.accelerationIncludingGravity || e.acceleration;
      if (!a) return;
      const g = 9.80665;
      const mag = Math.sqrt(
        (a.x || 0) ** 2 + (a.y || 0) ** 2 + (a.z || 0) ** 2
      ) / g;
      const now = Date.now();
      if (mag > PEAK_THRESHOLD && now - lastStepRef.current > COOLDOWN_MS) {
        lastStepRef.current = now;
        setSteps((s) => s + 1);
      }
    };
    handlerRef.current = onMotion;
    window.addEventListener("devicemotion", onMotion);
    setRunning(true);
  }

  async function stopAndLog() {
    detach();
    setRunning(false);
    if (steps > 0) {
      const session = `pedo-${startedAt}`;
      await onSubmit({ steps, source: "pedometer", session_id: session });
      setSteps(0);
    }
  }

  function discard() {
    detach();
    setRunning(false);
    setSteps(0);
  }

  return (
    <div
      data-testid="browser-pedometer"
      className="rounded-2xl border border-purple-200 bg-purple-50/40 p-5"
    >
      <div className="text-xs uppercase tracking-widest text-purple-700 mb-2">
        Live walk · in-browser pedometer
      </div>
      <div className="flex items-end gap-4">
        <div className="text-5xl font-black tabular-nums text-purple-900">
          {steps.toLocaleString()}
        </div>
        <div className="text-sm text-slate-500 pb-2">
          steps {running ? "(tracking…)" : "this session"}
        </div>
      </div>
      <div className="mt-4 flex gap-2 flex-wrap">
        {!running ? (
          <Button
            data-testid="pedometer-start-btn"
            onClick={start}
            disabled={disabled}
            className="bg-purple-700 hover:bg-purple-800 text-white"
          >
            Start tracking
          </Button>
        ) : (
          <>
            <Button
              data-testid="pedometer-stop-btn"
              onClick={stopAndLog}
              disabled={disabled || steps === 0}
              className="bg-pink-600 hover:bg-pink-700 text-white"
            >
              Stop &amp; log {steps.toLocaleString()} steps
            </Button>
            <Button
              data-testid="pedometer-discard-btn"
              onClick={discard}
              variant="outline"
            >
              Discard
            </Button>
          </>
        )}
      </div>
      {permission === "denied" && (
        <p className="mt-3 text-xs text-red-600">
          Motion access denied — use Manual entry instead, or enable motion
          access for this site in your browser settings.
        </p>
      )}
      <p className="mt-3 text-xs text-slate-500">
        Keep your phone in your pocket or hand. Accuracy ≈ 85%. For the most
        accurate count, log your daily total from Apple Health / Google Fit
        with Manual entry below.
      </p>
    </div>
  );
}
